import { ChevronLeft, ChevronRight } from "lucide-react";
import { type HTMLAttributes, type ReactNode, forwardRef } from "react";
import { cn } from "../cn";
import { Button } from "./Button";

type PaginationStatus = "ready" | "pending" | "error";

const PAGER_BUTTON_CLASS_NAME =
  "rounded-lg border-border/70 text-muted-foreground hover:border-border/70 hover:bg-muted/50 hover:text-foreground active:scale-100 disabled:opacity-40 [&_svg]:size-5";

export interface PaginationProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  readonly page: number;
  readonly pageSize: number;
  readonly totalItems: number;
  readonly status?: PaginationStatus;
  readonly onPageChange: (page: number) => void;
}

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      page,
      pageSize,
      totalItems,
      status = "ready",
      onPageChange,
      className,
      ...props
    },
    ref,
  ): ReactNode => {
    const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
    const visiblePage = Math.max(1, Math.min(page, pageCount));
    const rangeStart = totalItems === 0 ? 0 : (visiblePage - 1) * pageSize + 1;
    const rangeEnd = Math.min(visiblePage * pageSize, totalItems);
    const canGoPrevious = visiblePage > 1;
    const canGoNext = visiblePage < pageCount;
    const hasControls = canGoPrevious || canGoNext;

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-wrap items-center justify-between gap-3",
          className,
        )}
        {...props}
      >
        <p className="text-sm text-muted-foreground">
          {status === "ready"
            ? `${rangeStart}–${rangeEnd} van ${totalItems}`
            : "–"}
        </p>

        {hasControls && (
          <nav aria-label="Paginering" className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="icon-sm"
              className={PAGER_BUTTON_CLASS_NAME}
              aria-label="Vorige pagina"
              disabled={!canGoPrevious}
              onClick={() => onPageChange(visiblePage - 1)}
            >
              <ChevronLeft aria-hidden="true" className="size-5" />
            </Button>
            <span className="px-2 text-sm tabular-nums text-muted-foreground">
              Pagina {visiblePage} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              className={PAGER_BUTTON_CLASS_NAME}
              aria-label="Volgende pagina"
              disabled={!canGoNext}
              onClick={() => onPageChange(visiblePage + 1)}
            >
              <ChevronRight aria-hidden="true" className="size-5" />
            </Button>
          </nav>
        )}
      </div>
    );
  },
);
Pagination.displayName = "Pagination";
